/**
 * How people use the app, for us (backlog 1d013d63, owner 2026-10-23): one
 * shared product-analytics project for every screen, only for people who
 * agreed (Profile, or the one-time prompt). Events go to our API server
 * (POST /api/usage), which checks consent again and forwards them: the app
 * loads no analytics script and holds no key. It replaced the Google tag,
 * which counted the app's pages as tinycms.ai traffic.
 *
 * What is recorded: the screen ("/content/:id", ids taken out) and the app's
 * own buttons and tabs by their label. Never what anyone types, titles,
 * readers' data or emails.
 */

export type UsageEvent = {
  type:
    | 'Screen viewed'
    | 'Button clicked'
    | 'Tab clicked'
    | 'health_viewed'
    | 'health_area_filtered'
    | 'suggestion_opened'
    | 'suggestion_ask_agent'
    | 'suggestion_kept'
    | 'suggestion_reviewed'
    | 'health_check_now'
    | 'held_override';
  props: { screen?: string; label?: string; area?: string };
  time: number;
};

const FLUSH_MS = 5000;
const MAX_QUEUE = 50;
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

let consented = false;
let siteId: string | null = null;
let queue: UsageEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

/** The route with ids taken out: "/content/3c3c…" → "/content/:id". */
export function screenOf(pathname: string): string {
  return (
    (pathname || '/')
      .replace(UUID, ':id')
      .replace(/\/\d+(?=\/|$)/g, '/:n')
      .replace(/\/+$/, '') || '/'
  );
}

/**
 * A clicked control's label, when it's one of the app's own buttons or tabs
 * (short UI words, numbers as #); null for anything else, so no title or
 * text someone wrote is ever recorded.
 */
export function controlLabel(
  el: Element | null
): { type: UsageEvent['type']; label: string } | null {
  const control = el?.closest?.('.ink-btn, .ink-nav button, .ink-seg button') || null;
  if (!control) return null;
  const text = (control.getAttribute('aria-label') || control.textContent || '')
    .replace(/\d[\d,.]*/g, '#')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text || text.length > 40) return null;
  const tab = control.closest('.ink-nav, .ink-seg');
  return { type: tab ? 'Tab clicked' : 'Button clicked', label: text };
}

function backend() {
  return process.env.REACT_APP_BACKEND_HOST || 'http://localhost:4000';
}

function flush(keepalive = false) {
  if (timer) clearTimeout(timer);
  timer = null;
  if (!consented || !queue.length) {
    queue = [];
    return;
  }
  const events = queue;
  queue = [];
  void fetch(`${backend()}/api/usage`, {
    method: 'POST',
    keepalive,
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ events, siteId }),
  }).catch(() => undefined);
}

/** Whether this person agreed; false drops anything waiting. */
export function setUsageConsent(on: boolean) {
  consented = on;
  if (!on) queue = [];
}

export function setUsageSite(id: string | null) {
  siteId = id;
}

export function track(type: UsageEvent['type'], props: UsageEvent['props']) {
  if (!consented) return;
  queue.push({ type, props, time: Date.now() });
  if (queue.length >= MAX_QUEUE) flush();
  else if (!timer) timer = setTimeout(() => flush(), FLUSH_MS);
}

/** Send what's waiting before the tab goes away. */
export function flushUsage() {
  flush(true);
}
